"use client";

import { Suspense } from "react";
import { AppProviders } from "@/components/AppProviders";
import { DashboardShellFallback } from "@/app/DashboardShellFallback";
import {
  DashboardRouterProvider,
  useDashboardRouter,
} from "@/hooks/useDashboardRouter";
import { OrgUrlSyncProvider } from "@/components/OrgUrlSyncProvider";
import { SidebarLayout } from "@/components/SidebarLayout";
import { isCodesmithExtensionEmbed } from "@/lib/codesmithExtensionEmbed";

// The Chrome extension iframe (`?embed=extension`) renders the page bare, as
// `_app.tsx` does: no rail, sidebars, command palette, shortcuts modal, or
// the shell's prefetch hooks. The page still needs a Suspense boundary below
// AppProviders: pages that suspend at their root (the PR resolver reads
// useActiveGithubOrgForUser there) would otherwise bubble to the layout
// boundary, discarding the query client and auth tree and re-suspending on
// the fresh cache.
function DashboardShell({ children }: { children: React.ReactNode }) {
  const router = useDashboardRouter();
  return (
    <OrgUrlSyncProvider>
      {isCodesmithExtensionEmbed(router.query.embed) ? (
        <Suspense fallback={<DashboardShellFallback />}>{children}</Suspense>
      ) : (
        <SidebarLayout>{children}</SidebarLayout>
      )}
    </OrgUrlSyncProvider>
  );
}

export default function DashboardLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <Suspense fallback={<DashboardShellFallback />}>
      <DashboardRouterProvider>
        <AppProviders>
          <DashboardShell>{children}</DashboardShell>
        </AppProviders>
      </DashboardRouterProvider>
    </Suspense>
  );
}
